import React, { useEffect, useRef } from 'react';
import './StorefrontAd.css';

/**
 * TextAd - Sleek text-based ad with corner accents (Chowdeck style)
 * Compact, modern design that blends well with content
 */
const TextAd = ({
  ad,
  onView,
  onClick,
  className = ''
}) => {
  const adRef = useRef(null);
  const viewTracked = useRef(false);

  // Track view when ad comes into viewport
  useEffect(() => {
    if (!ad || viewTracked.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewTracked.current) {
            viewTracked.current = true;
            if (onView) {
              onView(ad.id);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    if (adRef.current) {
      observer.observe(adRef.current);
    }

    return () => {
      if (adRef.current) {
        observer.unobserve(adRef.current);
      }
    };
  }, [ad, onView]);

  if (!ad) return null;

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick(ad.id, ad.ctaLink);
    }
  };

  const bgColor = ad.backgroundColor || '#f8fafc';
  const textColor = ad.textColor || '#1e293b';
  const accentColor = ad.accentColor || '#f97316';

  return (
    <a
      ref={adRef}
      href={ad.ctaLink}
      onClick={handleClick}
      className={`text-ad ad-fade-in ${className}`}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        '--accent-color': accentColor
      }}
    >
      {/* Left accent bar */}
      <style>
        {`
          .text-ad[style*="${bgColor}"]::before {
            background: ${accentColor};
          }
          .text-ad[style*="${bgColor}"]::after {
            background: ${accentColor};
            opacity: 0.15;
          }
        `}
      </style>

      <div className="text-ad-content">
        <h4 className="text-ad-headline" style={{ color: textColor }}>
          {ad.headline}
        </h4>
        {ad.description && (
          <p className="text-ad-description" style={{ color: textColor }}>
            {ad.description}
          </p>
        )}
      </div>

      <div className="text-ad-right">
        {ad.ctaText && (
          <span
            className="text-ad-cta"
            style={{
              backgroundColor: accentColor,
              color: '#ffffff'
            }}
          >
            {ad.ctaText}
            <svg
              className="text-ad-cta-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </span>
        )}
        {ad.sponsor && (
          <span className="text-ad-sponsor" style={{ color: textColor, opacity: 0.6 }}>
            Sponsored by {ad.sponsor}
          </span>
        )}
      </div>
    </a>
  );
};

export default TextAd;
